// src/components/RegisterOrganizationPage.js (or RegisterOrganization.js)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle, faCheckCircle, faChurch, faUserShield, faXmark } from '@fortawesome/free-solid-svg-icons';

// Import Firebase services
import { functions, auth } from '../firebase'; // Added auth
import { httpsCallable } from 'firebase/functions';
// Added Auth functions for sign-in and verification
import { signInWithEmailAndPassword } from "firebase/auth";

// Define the callable function reference
const registerOrgCallable = httpsCallable(functions, 'registerOrganization');

// US States array for the dropdown - FULL LIST
const usStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "District Of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
    "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
    "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
    "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

// US Timezones
const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Arizona Time (AZ)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
    { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)' },
];

function RegisterOrganizationPage() {
    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // State variables
    const [churchName, setChurchName] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [timezone, setTimezone] = useState('');
    const [adminName, setAdminName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [adminConfirmPassword, setAdminConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null); // Use null for initial state
    const [successMessage, setSuccessMessage] = useState(null); // Use null for initial state

    const navigate = useNavigate();
    const errorRef = useRef(null);

    // Scroll to error when it appears
    useEffect(() => {
        if (error && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [error]);

    // Form Validation function
    const validateForm = () => {
        setError(null);
        if (!churchName.trim() || !streetAddress.trim() || !city.trim() || !state || !zipCode.trim() ||
            !adminName.trim() || !adminEmail.trim() || !adminPassword.trim() || !adminConfirmPassword.trim())
            { setError("All fields are required."); return false; }
        if (!/\S+@\S+\.\S+/.test(adminEmail)) { setError("Please enter a valid administrator email address."); return false; }
        if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) { setError("Please enter a valid 5-digit Zip Code."); return false; }
        if (adminPassword.length < 6) { setError("Administrator password must be at least 6 characters long."); return false; }
        if (adminPassword !== adminConfirmPassword) { setError("Administrator passwords do not match."); return false; }
        return true;
    };

    // Handle Form Submission
    const handleSubmit = async (event) => { // Remove event type in JS
        event.preventDefault();
        if (!validateForm()) { return; }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        // Track registration attempt
        if (window.gtag) {
            window.gtag('event', 'begin_registration', {
                event_category: 'acquisition',
                event_label: 'features_page',
            });
        }

        const currentAdminEmail = adminEmail.trim();
        const currentAdminPassword = adminPassword;

        const payload = {
            churchInfo: { name: churchName.trim(), address: streetAddress.trim(), city: city.trim(), state: state, zip: zipCode.trim(), timezone: timezone || undefined },
            adminInfo: { name: adminName.trim(), email: currentAdminEmail, password: currentAdminPassword }
        };
        console.log("Calling 'registerOrganization' function with payload:", { ...payload, adminInfo: { ...payload.adminInfo, password: '***' } });

        try {
            // Step 1: Call backend function
            const result = await registerOrgCallable(payload);
            const resultData = result.data;
            console.log("'registerOrganization' function result:", resultData);

            // Use optional chaining - safe in JS
            if (resultData?.success) {
                // Step 2: Attempt Client-Side Sign-In
                console.log(`Org registered. Attempting client-side sign in for admin ${currentAdminEmail}...`);
                signInWithEmailAndPassword(auth, currentAdminEmail, currentAdminPassword)
                    .then((userCredential) => {
                        console.log(`Client-side sign in successful for admin user ${userCredential.user.uid}. Redirecting to dashboard...`);
                        // Google Ads conversion tracking
                        if (window.gtag) {
                            window.gtag('event', 'complete_registration', {
                                event_category: 'acquisition',
                                event_label: 'church_registration_complete',
                            });
                        }
                        setSuccessMessage(`Organization registered! Redirecting to dashboard...`);
                        // Clear form
                        setChurchName(''); setStreetAddress(''); setCity(''); setState(''); setZipCode(''); setTimezone('');
                        setAdminName(''); setAdminEmail(''); setAdminPassword(''); setAdminConfirmPassword('');
                        // Redirect to dashboard
                        setTimeout(() => { navigate('/dashboard'); }, 2000);
                    })
                    .catch((signInError) => {
                        console.error("Error signing in new admin user after registration:", signInError);
                        setError(`Organization registered, but failed to sign the admin in automatically: ${signInError.message}. Please try logging in manually.`);
                        setIsLoading(false);
                    });

            } else {
                setError(resultData?.message || "Failed to register organization. Please try again.");
                setIsLoading(false);
            }
        } catch (err) { // Use 'err' without type or check structure
            console.error("Error calling 'registerOrganization' function:", err);
            let specificError = "An unexpected error occurred during registration.";
             if (err && err.code && err.message) { // Basic check for error structure
                 const code = err.code;
                 const message = err.message;
                 if (code === 'functions/already-exists' || message.includes('EMAIL_EXISTS')) { specificError = "An administrator account with this email already exists."; }
                 else if (message.includes('organization name already exists')) { specificError = "An organization with this name already exists."; }
                 else if (code === 'functions/invalid-argument') { specificError = "Invalid data provided. Please check your input."; }
                 else { specificError = `Server error: ${message} (code: ${code})`; }
            }
            setError(specificError);
            setIsLoading(false);
        }
    };

     // Determine if form inputs should be disabled
    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <div className="register-page">
            {/* Hero Header */}
            <section className="register-hero">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center">
                            <h1 className="register-title">
                                <FontAwesomeIcon icon={faChurch} className="me-3" />
                                Create Your Church Directory
                            </h1>
                            <p className="register-subtitle">
                                Set up a new directory for your church and start connecting your congregation today.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="register-content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            {/* Member Redirect Notice */}
                            <p className="member-redirect">
                                Looking to join an existing directory? <Link to="/create-account">Sign up as a member</Link>
                            </p>

                            {/* Registration Card */}
                            <div className="register-card">
                                {/* Error Display */}
                                {error && (
                                    <div ref={errorRef} className="alert alert-danger d-flex align-items-center" role="alert">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" />
                                        <div>{error}</div>
                                    </div>
                                )}

                                {/* Success Display */}
                                {successMessage && (
                                    <div className="alert alert-success d-flex align-items-center" role="alert">
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 flex-shrink-0" />
                                        <div>{successMessage}</div>
                                    </div>
                                )}

                                {/* Registration Form */}
                                {!successMessage && (
                                    <form onSubmit={handleSubmit} noValidate>
                                        {/* Church Info Section */}
                                        <div className="form-section">
                                            <div className="section-header">
                                                <div className="section-icon">
                                                    <FontAwesomeIcon icon={faChurch} />
                                                </div>
                                                <h3>Church Information</h3>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="churchName" className="form-label">Church Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="churchName"
                                                    placeholder="First Baptist Church"
                                                    value={churchName}
                                                    onChange={(e) => setChurchName(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="streetAddress" className="form-label">Street Address</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="streetAddress"
                                                    placeholder="123 Main Street"
                                                    value={streetAddress}
                                                    onChange={(e) => setStreetAddress(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="city" className="form-label">City</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="city"
                                                        placeholder="Springfield"
                                                        value={city}
                                                        onChange={(e) => setCity(e.target.value)}
                                                        required
                                                        disabled={formInputsDisabled}
                                                    />
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label htmlFor="state" className="form-label">State</label>
                                                    <select
                                                        className="form-select"
                                                        id="state"
                                                        value={state}
                                                        onChange={(e) => setState(e.target.value)}
                                                        required
                                                        disabled={formInputsDisabled}
                                                    >
                                                        <option value="" disabled>Select...</option>
                                                        {usStates.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label htmlFor="zipCode" className="form-label">Zip Code</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="zipCode"
                                                        placeholder="12345"
                                                        value={zipCode}
                                                        onChange={(e) => setZipCode(e.target.value)}
                                                        required
                                                        disabled={formInputsDisabled}
                                                        inputMode="numeric"
                                                        pattern="[0-9]{5}"
                                                        maxLength="5"
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="timezone" className="form-label">Timezone</label>
                                                <select
                                                    className="form-select"
                                                    id="timezone"
                                                    value={timezone}
                                                    onChange={(e) => setTimezone(e.target.value)}
                                                    disabled={formInputsDisabled}
                                                >
                                                    <option value="">Select timezone (optional)...</option>
                                                    {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                                                </select>
                                                <div className="form-text">Used for birthday and anniversary notifications</div>
                                            </div>
                                        </div>

                                        {/* Administrator Info Section */}
                                        <div className="form-section">
                                            <div className="section-header">
                                                <div className="section-icon">
                                                    <FontAwesomeIcon icon={faUserShield} />
                                                </div>
                                                <h3>Administrator Account</h3>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="adminName" className="form-label">Full Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="adminName"
                                                    placeholder="John Smith"
                                                    value={adminName}
                                                    onChange={(e) => setAdminName(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="adminEmail" className="form-label">Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="adminEmail"
                                                    placeholder="admin@church.org"
                                                    value={adminEmail}
                                                    onChange={(e) => setAdminEmail(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                                <div className="form-text">Used for login and important notifications</div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="adminPassword" className="form-label">Password</label>
                                                    <input
                                                        type="password"
                                                        className={`form-control ${adminPassword.length >= 6 ? 'is-valid' : ''}`}
                                                        id="adminPassword"
                                                        placeholder="Create a password"
                                                        value={adminPassword}
                                                        onChange={(e) => setAdminPassword(e.target.value)}
                                                        required
                                                        disabled={formInputsDisabled}
                                                    />
                                                    <div className="form-text">At least 6 characters</div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="adminConfirmPassword" className="form-label">Confirm Password</label>
                                                    <div className="password-input-wrapper">
                                                        <input
                                                            type="password"
                                                            className={`form-control ${adminConfirmPassword ? (adminPassword === adminConfirmPassword ? 'is-valid' : 'is-invalid') : ''}`}
                                                            id="adminConfirmPassword"
                                                            placeholder="Confirm password"
                                                            value={adminConfirmPassword}
                                                            onChange={(e) => setAdminConfirmPassword(e.target.value)}
                                                            required
                                                            disabled={formInputsDisabled}
                                                        />
                                                        {adminConfirmPassword && (
                                                            <span className={`password-match-icon ${adminPassword === adminConfirmPassword ? 'match' : 'no-match'}`}>
                                                                <FontAwesomeIcon icon={adminPassword === adminConfirmPassword ? faCheckCircle : faXmark} />
                                                            </span>
                                                        )}
                                                    </div>
                                                    {adminConfirmPassword && adminPassword !== adminConfirmPassword && (
                                                        <div className="password-mismatch-text">Passwords do not match</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="d-grid">
                                            <button type="submit" className="btn btn-register" disabled={formInputsDisabled}>
                                                {isLoading ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                        Creating Your Directory...
                                                    </>
                                                ) : (
                                                    "Create Church Directory"
                                                )}
                                            </button>
                                        </div>

                                        {/* Trial Note */}
                                        <p className="trial-note">
                                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                            First 3 months free. No credit card required.
                                        </p>
                                    </form>
                                )}

                                {/* Footer Links */}
                                <div className="register-footer">
                                    <p>
                                        Already have an account? <Link to="/">Sign In</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default RegisterOrganizationPage;